import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import * as api from '../services/api';
import Icons from '../components/Icons';
import Lightbox from '../components/Lightbox';
import { groupItemsByDate } from '../utils/grouping';
import { confirmAlert } from '../utils/confirmAlert';
import toast from 'react-hot-toast';

export default function GalleryPage() {
  const { token } = useAuth();
  const { items, refreshItems, removeItem } = useCredits();
  const [tab, setTab] = useState('All');
  const [lbItem, setLbItem] = useState(null);
  
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const filtered = items.filter(i =>
    tab === 'All' ||
    (tab === 'Images' && i.type !== 'video') ||
    (tab === 'Videos' && i.type === 'video')
  );

  const handleDelete = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try {
      await api.deleteGeneration(token, id);
      removeItem(id);
      setLbItem(null);
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    const isConfirmed = await confirmAlert({
      title: 'Delete Selected Items?',
      message: `Are you sure you want to delete ${selectedIds.length} items? This action cannot be undone.`
    });
    if (!isConfirmed) return;
    setIsDeleting(true);
    try {
      await api.batchDeleteGenerations(token, selectedIds);
      selectedIds.forEach(id => removeItem(id));
      setSelectedIds([]);
      setIsSelectionMode(false);
      toast.success(`Successfully deleted ${selectedIds.length} items`);
    } catch {
      toast.error('Batch delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBatchDownload = async () => {
    if (selectedIds.length === 0) return;
    setIsDownloading(true);
    try {
      const itemsToDownload = filtered.filter(i => selectedIds.includes(i.id));
      for (let i = 0; i < itemsToDownload.length; i++) {
        const itemContext = itemsToDownload[i];
        const url = itemContext.videoUrl || itemContext.imageUrl;
        const isImage = !itemContext.videoUrl;
        
        try {
           const API_URL = process.env.REACT_APP_API_URL || '/api';
           const res = await fetch(`${API_URL}/download?url=${encodeURIComponent(url)}`, {
             headers: { Authorization: `Bearer ${token}` },
           });
           if (res.ok) {
             const blob = await res.blob();
             const ext = isImage ? 'png' : 'mp4';
             const a = document.createElement('a');
             a.href = URL.createObjectURL(blob);
             a.download = `aifield_${Date.now()}_${i}.${ext}`;
             document.body.appendChild(a);
             a.click();
             document.body.removeChild(a);
             window.URL.revokeObjectURL(a.href);
           }
        } catch (e) {
           console.error('Download err:', e);
        }
        await new Promise(r => setTimeout(r, 400));
      }
      toast.success(`Started download for ${itemsToDownload.length} items`);
      setIsSelectionMode(false);
      setSelectedIds([]);
    } catch {
      toast.error('Batch download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const startLongPress = (id) => {
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setIsSelectionMode(true);
      setSelectedIds(prev => prev.includes(id) ? prev : [...prev, id]);
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  return (
    <>
      <div className="main" style={{ flex: 1 }}>
        <div className="mtbar">
          {['All', 'Images', 'Videos'].map(t => (
            <div key={t} className={`mtab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>{t}</div>
          ))}
          <div style={{ flex: 1 }} />
          <button 
            onClick={() => {
              setIsSelectionMode(!isSelectionMode);
              if (isSelectionMode) setSelectedIds([]);
            }}
            style={{
               background: isSelectionMode ? 'var(--s1)' : 'transparent',
               border: '1px solid var(--b1)',
               color: 'var(--t1)',
               padding: '4px 12px',
               borderRadius: 20,
               fontSize: 12,
               cursor: 'pointer',
               marginRight: 12
            }}
          >
            {isSelectionMode ? 'Cancel' : 'Select'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>{filtered.length} items</span>
        </div>
        <div className="mbody" onClick={() => {
          if (isSelectionMode) {
            setIsSelectionMode(false);
            setSelectedIds([]);
          }
        }}>
          {filtered.length > 0 ? (
            <div className="grouped-collections">
              {groupItemsByDate(filtered).map(group => (
                <div key={group.label} className="date-group-wrap">
                  <div className="date-group-label">{group.label}</div>
                  <div className="ggrid">
                    {group.items.map(i => (
                      <div key={i.id} className="gitem" 
                        onPointerDown={(e) => {
                          if (e.pointerType === 'mouse' && e.button !== 0) return;
                          startLongPress(i.id);
                        }}
                        onPointerUp={cancelLongPress}
                        onPointerLeave={cancelLongPress}
                        onPointerCancel={cancelLongPress}
                        onContextMenu={e => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (longPressFired.current) {
                            e.preventDefault();
                            setTimeout(() => { longPressFired.current = false; }, 50);
                            return;
                          }
                          if (isSelectionMode) {
                            const isRemoving = selectedIds.includes(i.id);
                            const next = isRemoving ? selectedIds.filter(id => id !== i.id) : [...selectedIds, i.id];
                            if (next.length === 0) setIsSelectionMode(false);
                            setSelectedIds(next);
                          } else {
                            setLbItem(i);
                          }
                        }}
                        style={{ 
                          border: selectedIds.includes(i.id) ? '2px solid #3b82f6' : '1px solid transparent',
                          transform: selectedIds.includes(i.id) ? 'scale(0.96)' : 'none',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                      >
                        {isSelectionMode && (
                          <div style={{
                            position: 'absolute', top: 8, right: 8, width: 22, height: 22,
                            borderRadius: '50%', background: selectedIds.includes(i.id) ? '#3b82f6' : 'rgba(0,0,0,0.5)',
                            border: '2px solid #fff', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            {selectedIds.includes(i.id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                          </div>
                        )}
                        {i.type === 'video' && i.videoUrl ? (
                          <video src={i.videoUrl} muted loop playsInline onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <img src={i.imageUrl} alt="" />
                        )}
                        <div className="gbadge" style={{
                          background: i.type === 'video' ? 'rgba(34,211,238,.12)' : i.type === 'multishot' ? 'rgba(244,114,182,.14)' : 'rgba(139,92,246,.12)',
                          color: i.type === 'video' ? '#22d3ee' : i.type === 'multishot' ? '#f472b6' : '#8b5cf6'
                        }}>
                          {i.type === 'video' ? '▶ Video' : '🖼'}
                        </div>
                        <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                          <div className="gm">
                            <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {i.prompt}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{i.modelName || i.model}</span>
                              
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '80px 16px', color: 'var(--t3)' }}>
              <Icons.Img sz={48} />
              <p style={{ marginTop: 12, fontSize: 15, fontWeight: 600 }}>No creations yet</p>
              <p style={{ fontSize: 12, marginTop: 4 }}>Start generating images and videos to see them here</p>
            </div>
          )}
        </div>
      </div>

      {isSelectionMode && selectedIds.length > 0 && (
        <div style={{
           position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
           background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 12,
           padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16,
           boxShadow: '0 10px 40px rgba(0,0,0,0.5)', zIndex: 90
        }}>
           <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedIds.length} selected</div>
           
           <div style={{ display: 'flex', gap: 8 }}>
             <button 
               disabled={isDownloading || isDeleting}
               onClick={handleBatchDownload}
               style={{
                 background: isDownloading ? 'rgba(59,130,246,0.5)' : '#3b82f6', color: '#fff', border: 'none', padding: '6px 16px',
                 borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: isDownloading ? 'not-allowed' : 'pointer',
                 display: 'flex', alignItems: 'center', gap: 6
               }}
             >
               <Icons.DL sz={14} /> {isDownloading ? 'Downloading...' : 'Download'}
             </button>
             
             <button 
               disabled={isDeleting || isDownloading}
               onClick={handleBatchDelete}
               style={{
                 background: isDeleting ? 'rgba(239,68,68,0.5)' : '#ef4444', color: '#fff', border: 'none', padding: '6px 16px',
                 borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer'
               }}
             >
               {isDeleting ? 'Deleting...' : 'Delete Selected'}
             </button>
           </div>
        </div>
      )}

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDelete} />
    </>
  );
}
